import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'fast-deep-equal';
import { bind, clear } from 'size-sensor';
import { pick } from './utils'; 

const EchartsReactCore = (props) => {
  const {
    echarts,
    option,
    notMerge,
    lazyUpdate,
    style,
    className,
    theme,
    onChartReady,
    showLoading,
    loadingOption,
    onEvents,
    opts,
    shouldSetOption
  } = props;

  const echartsElement = useRef(null); 
  const prevPropsRef = useRef({}); 

  const dispose = () => {
    if (echartsElement.current) {
      try {
        clear(echartsElement.current);
      } catch (e) {
        console.warn(e);
      }
      echarts.dispose(echartsElement.current);
    }
  };

  // Function to get ECharts instance
  const getEchartsInstance = () =>
    echarts.getInstanceByDom(echartsElement.current) ||
    echarts.init(echartsElement.current, theme, opts);

  // Function to bind events to the ECharts instance
  const bindEvents = (instance, events) => {
    for (const eventName in events) {
      if (Object.prototype.hasOwnProperty.call(events, eventName)) {
        const func = events[eventName];
        if (typeof eventName === 'string' && typeof func === 'function') {
          instance.on(eventName, (param) => func(param, instance));
        }
      }
    }
  };

  // Function to render ECharts DOM and set options
  const renderEchartDom = () => {
    const echartObj = getEchartsInstance();

    echartObj.setOption(option, notMerge || false, lazyUpdate || false);

    if (showLoading) {
      echartObj.showLoading(loadingOption || null);
    } else {
      echartObj.hideLoading();
    }

    return echartObj;
  };

 
  const rerender = () => {
    const echartObj = renderEchartDom();

   
    bindEvents(echartObj, onEvents || {});

    if (typeof onChartReady === 'function') {
      onChartReady(echartObj);
    }

   
    if (echartsElement.current) {
      bind(echartsElement.current, () => {
        try {
          echartObj.resize();
        } catch (e) {
          console.warn(e);
        }
      });
    }
  };

  useEffect(() => {
    rerender();
  }, []); 

  
  useEffect(() => {
    dispose();
    
    const pickKeys = ['option', 'notMerge', 'lazyUpdate', 'showLoading', 'loadingOption'];
    
    if (isEqual(pick(props, pickKeys), pick(prevPropsRef.current, pickKeys))) {
      return; 
    }

    if (
      typeof shouldSetOption === 'function' &&
      !shouldSetOption(prevPropsRef.current, props)
    ) {
      return; 
    }

    rerender();

    prevPropsRef.current = props;
    
    return () => dispose(); 
  }, [theme, opts, option, notMerge, lazyUpdate, showLoading, loadingOption]); 

  return (
    <div
      ref={echartsElement}
      style={{ height: 300, ...style }}
      className={`echarts-for-react ${className}`}
    />
  );
};

EchartsReactCore.propTypes = {
  option: PropTypes.object.isRequired,
  echarts: PropTypes.object.isRequired,
  notMerge: PropTypes.bool,
  lazyUpdate: PropTypes.bool,
  style: PropTypes.object,
  className: PropTypes.string,
  theme: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onChartReady: PropTypes.func,
  showLoading: PropTypes.bool,
  loadingOption: PropTypes.object,
  onEvents: PropTypes.object,
  opts: PropTypes.shape({
    devicePixelRatio: PropTypes.number,
    renderer: PropTypes.oneOf(['canvas', 'svg']),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null, undefined, 'auto'])]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null, undefined, 'auto'])])
  }),
  shouldSetOption: PropTypes.func
};

EchartsReactCore.defaultProps = {
  notMerge: false,
  lazyUpdate: false,
  style: {},
  className: '',
  theme: null,
  onChartReady: () => {},
  showLoading: false,
  loadingOption: null,
  onEvents: {},
  opts: {},
  shouldSetOption: () => true
};

export default EchartsReactCore;
